import type { DropResult } from '@hello-pangea/dnd';
import type { ZodValidator } from '@tanstack/zod-form-adapter';

import { Label } from '@connected/form-fields/components/label';
import { SingleHorizontalOptionItem, SingleOptionField } from '@connected/form-fields/components/option';
import { Button } from '@connected/ui/components/button';
import { Table, TableCell, TableHead, TableHeader, TableRow } from '@connected/ui/components/table';
import { Toggle } from '@connected/ui/components/toggle';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import { useForm } from '@tanstack/react-form';
import { zodValidator } from '@tanstack/zod-form-adapter';
import { GripVertical, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useRisoMeasurements } from '@/hooks/queries/use-riso-measurements';
import { showConfirm } from '@/stores/confirmer';

import type { RisoMeasurementFormProps, RisoMeasurementFormValues, RisoMeasurementSubFormValues } from '../utils/riso-measurement-types';

import { useCreateRisoMeasurement, useDeleteRisoMeasurement, useUpdateRisoMeasurementOrder } from '../hooks/use-riso-measurement-mutations';
import { circuitKindOptions } from '../utils/riso-measurement-constants';
import { RisoMeasurementRow } from './riso-measurement-row';

const VALID_NUMBER_OF_MEASUREMENTS = [2, 3, 10] as const;

export function RisoMeasurementList({
    electricalProjectVersionId,
    distributionBoardDeviceId,
}: RisoMeasurementFormProps) {
    const [isDragDropEnabled, setIsDragDropEnabled] = useState(false);

    const listQuery = useRisoMeasurements({
        electricalProjectVersionId,
        distributionBoardDeviceId,
    });

    const { updateOrder } = useUpdateRisoMeasurementOrder({
        electricalProjectVersionId,
    });

    const { mutation: createMutation } = useCreateRisoMeasurement({
        electricalProjectVersionId,
    });

    const { mutation: deleteMutation } = useDeleteRisoMeasurement({
        electricalProjectVersionId,
    });

    const defaultValues = useMemo<RisoMeasurementSubFormValues>(() => {
        const measurements = listQuery.data?.measurements || [];

        // If no measurements, leave numberOfMeasurements as null so user can pick
        // Otherwise, find the closest valid number
        const numberOfMeasurements = measurements.length === 0
            ? null
            : VALID_NUMBER_OF_MEASUREMENTS.reduce((closest, validNumber) => {
                    return Math.abs(validNumber - measurements.length) < Math.abs(closest - measurements.length)
                        ? validNumber
                        : closest;
                }, VALID_NUMBER_OF_MEASUREMENTS[0]);

        return {
            numberOfMeasurements,
            measurements: [
                ...(measurements.map(measurement => ({
                    ElectricalProjectRisoMeasurementId: measurement.ElectricalProjectRisoMeasurementId || '',
                    order: measurement.Order || 0,
                    circuitKind: measurement.CircuitKind || null,
                    measuredValue: measurement.MeasuredValue || null,
                    minimumValue: measurement.MinimumValue || null,
                    isFaulty: measurement.IsFaulty || false,
                }))),
            ],
        };
    }, [listQuery.data?.measurements]);

    const form = useForm<RisoMeasurementSubFormValues, ZodValidator>({
        defaultValues,
        validatorAdapter: zodValidator(),
    });

    // Track if we're currently creating measurements to avoid duplicate calls
    const isCreatingRef = useRef(false);

    // Sync form state when query data changes
    useEffect(() => {
        if (!listQuery.data?.measurements)
            return;

        const newMeasurements: RisoMeasurementFormValues[] = listQuery.data.measurements.map(measurement => ({
            ElectricalProjectRisoMeasurementId: measurement.ElectricalProjectRisoMeasurementId || '',
            order: measurement.Order || 0,
            circuitKind: measurement.CircuitKind || null,
            measuredValue: measurement.MeasuredValue || null,
            minimumValue: measurement.MinimumValue || null,
            isFaulty: measurement.IsFaulty || false,
        }));

        form.setFieldValue('measurements', newMeasurements);
    }, [listQuery.data?.measurements, form]);

    async function createMissingMeasurements(targetCount: number, currentCount: number) {
        if (isCreatingRef.current)
            return;
        if (targetCount <= currentCount)
            return;

        isCreatingRef.current = true;
        try {
            const measurementsToCreate = targetCount - currentCount;
            for (let i = 0; i < measurementsToCreate; i++) {
                const measurementIndex = currentCount + i;
                const defaultCircuitKind = circuitKindOptions[measurementIndex]?.value ?? null;

                await createMutation.mutateAsync({
                    body: {
                        ElectricalProjectDistributionBoardDeviceId: distributionBoardDeviceId,
                        ElectricalProjectRisoMeasurementId: crypto.randomUUID(),
                        CircuitKind: defaultCircuitKind,
                        MeasuredValue: null,
                        MinimumValue: null,
                        IsFaulty: null,
                        Order: measurementIndex + 1,
                    },
                });
            }
        }
        finally {
            isCreatingRef.current = false;
        }
    }

    async function handleNumberOfMeasurementsChange(newCount: number | null) {
        if (newCount === null)
            return;

        const currentMeasurements = form.getFieldValue('measurements');
        const currentCount = currentMeasurements.length;

        if (newCount > currentCount) {
            await createMissingMeasurements(newCount, currentCount);
        }
        // When decreasing, measurements beyond newCount will be shown in unused section
    }

    function handleDragEnd(result: DropResult) {
        if (!result.destination) {
            return;
        }

        const sourceIndex = result.source.index;
        const destinationIndex = result.destination.index;

        if (sourceIndex === destinationIndex) {
            return;
        }

        const currentMeasurements = form.getFieldValue('measurements');
        const numberOfMeasurements = form.getFieldValue('numberOfMeasurements') ?? 0;
        const sortedMeasurements = [...currentMeasurements].sort((a, b) => a.order - b.order);
        const activeMeasurements = sortedMeasurements.slice(0, numberOfMeasurements);

        // Reorder the array
        const [removed] = activeMeasurements.splice(sourceIndex, 1);
        activeMeasurements.splice(destinationIndex, 0, removed);

        // Update order values for active measurements
        const updatedActiveMeasurements = activeMeasurements.map((measurement, index) => ({
            ...measurement,
            order: index + 1,
        }));

        // Keep unused measurements with their order (beyond active count)
        const unusedMeasurements = sortedMeasurements.slice(numberOfMeasurements).map((measurement, index) => ({
            ...measurement,
            order: numberOfMeasurements + index + 1,
        }));

        const allMeasurements = [...updatedActiveMeasurements, ...unusedMeasurements];

        // Update form state
        form.setFieldValue('measurements', allMeasurements);

        // Prepare batch update for backend
        const updates = allMeasurements.map(measurement => ({
            ElectricalProjectRisoMeasurementId: measurement.ElectricalProjectRisoMeasurementId,
            Order: measurement.order,
        }));

        // Send batch update to backend
        updateOrder(updates);
    }

    async function handleDeleteUnusedMeasurement(measurementId: string) {
        if (await showConfirm('Weet je zeker dat je deze meting wilt verwijderen?')) {
            deleteMutation.mutate({
                params: {
                    query: {
                        ElectricalProjectRisoMeasurementId: measurementId,
                    },
                },
            });
        }
    }

    async function handleDeleteAllUnusedMeasurements() {
        const currentMeasurements = form.getFieldValue('measurements');
        const numberOfMeasurements = form.getFieldValue('numberOfMeasurements') ?? 0;
        const sortedMeasurements = [...currentMeasurements].sort((a, b) => a.order - b.order);
        const unusedMeasurements = sortedMeasurements.slice(numberOfMeasurements);

        if (unusedMeasurements.length === 0)
            return;

        if (await showConfirm(`Weet je zeker dat je alle ${unusedMeasurements.length} ongebruikte metingen wilt verwijderen?`)) {
            await Promise.all(
                unusedMeasurements.map(measurement =>
                    deleteMutation.mutateAsync({
                        params: {
                            query: {
                                ElectricalProjectRisoMeasurementId: measurement.ElectricalProjectRisoMeasurementId,
                            },
                        },
                    }),
                ),
            );
        }
    }

    return (
        <DragDropContext onDragEnd={handleDragEnd}>
            <div className="flex flex-col gap-4">
                <div className="flex flex-row items-end justify-between gap-4 px-4">
                    <form.Field
                        name="numberOfMeasurements"
                        children={field => (
                            <Label label="Aantal metingen">
                                <SingleOptionField
                                    state={field.state}
                                    handleChange={(value) => {
                                        field.handleChange(value);
                                        handleNumberOfMeasurementsChange(value);
                                    }}
                                    options={VALID_NUMBER_OF_MEASUREMENTS.map(value => ({
                                        value,
                                        label: `${value} metingen`,
                                    }))}
                                    render={SingleHorizontalOptionItem}
                                    className="w-full flex flex-row"
                                />
                            </Label>
                        )}
                    />
                    <Toggle
                        variant="outline"
                        pressed={isDragDropEnabled}
                        onPressedChange={setIsDragDropEnabled}
                        aria-label="Volgorde wijzigen"
                    >
                        <GripVertical className="h-4 w-4" />
                        Volgorde wijzigen
                    </Toggle>
                </div>

                <form.Subscribe
                    selector={state => ({
                        measurements: state.values.measurements,
                        numberOfMeasurements: state.values.numberOfMeasurements,
                    })}
                >
                    {({ measurements, numberOfMeasurements }) => {
                        const activeCount = numberOfMeasurements ?? 0;
                        const sortedMeasurements = [...measurements].sort((a, b) => a.order - b.order);
                        const activeMeasurements = sortedMeasurements.slice(0, activeCount);
                        const unusedMeasurements = sortedMeasurements.slice(activeCount);

                        return (
                            <>
                                {activeMeasurements.length > 0 && (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                {isDragDropEnabled && <TableHead className="w-10" />}
                                                <TableHead>Meting</TableHead>
                                                <TableHead>Gemeten waarde</TableHead>
                                                <TableHead>Minimale waarde</TableHead>
                                                <TableHead>Akkoord</TableHead>
                                                <TableHead className="w-10" />
                                            </TableRow>
                                        </TableHeader>
                                        <Droppable droppableId="measurements">
                                            {provided => (
                                                <tbody
                                                    ref={provided.innerRef}
                                                    {...provided.droppableProps}
                                                    className="[&_tr:last-child]:border-0"
                                                >
                                                    {activeMeasurements.map((field, index) => {
                                                        const originalIndex = measurements.findIndex(
                                                            m => m.ElectricalProjectRisoMeasurementId === field.ElectricalProjectRisoMeasurementId,
                                                        );
                                                        return (
                                                            <Draggable
                                                                key={field.ElectricalProjectRisoMeasurementId}
                                                                draggableId={field.ElectricalProjectRisoMeasurementId}
                                                                index={index}
                                                                isDragDisabled={!isDragDropEnabled}
                                                            >
                                                                {(provided, snapshot) => (
                                                                    <RisoMeasurementRow
                                                                        innerRef={provided.innerRef}
                                                                        index={originalIndex}
                                                                        electricalProjectRisoMeasurementId={field.ElectricalProjectRisoMeasurementId}
                                                                        electricalProjectVersionId={electricalProjectVersionId}
                                                                        form={form}
                                                                        dragHandleProps={isDragDropEnabled ? provided.dragHandleProps : null}
                                                                        draggableProps={provided.draggableProps}
                                                                        isDragging={snapshot.isDragging}
                                                                        isDragDropEnabled={isDragDropEnabled}
                                                                    />
                                                                )}
                                                            </Draggable>
                                                        );
                                                    })}
                                                    {provided.placeholder}
                                                </tbody>
                                            )}
                                        </Droppable>
                                    </Table>
                                )}

                                {unusedMeasurements.length > 0 && (
                                    <div className="flex flex-col gap-2 mt-4">
                                        <div className="flex flex-row justify-between items-center px-4">
                                            <p className="text-sm font-medium text-muted-foreground">
                                                Ongebruikte metingen (
                                                {unusedMeasurements.length}
                                                )
                                            </p>
                                            <Button
                                                variant="destructive"
                                                size="sm"
                                                onClick={handleDeleteAllUnusedMeasurements}
                                            >
                                                <Trash2 className="h-4 w-4 mr-1" />
                                                Alles verwijderen
                                            </Button>
                                        </div>
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>Meting</TableHead>
                                                    <TableHead>Gemeten waarde</TableHead>
                                                    <TableHead>Minimale waarde</TableHead>
                                                    <TableHead>Akkoord</TableHead>
                                                    <TableHead className="w-10" />
                                                </TableRow>
                                            </TableHeader>
                                            <tbody className="[&_tr:last-child]:border-0">
                                                {unusedMeasurements.map(field => (
                                                    <TableRow key={field.ElectricalProjectRisoMeasurementId} className="opacity-60">
                                                        <TableCell>
                                                            {field.circuitKind || '-'}
                                                        </TableCell>
                                                        <TableCell>
                                                            {field.measuredValue ?? '-'}
                                                        </TableCell>
                                                        <TableCell>
                                                            {field.minimumValue ?? '-'}
                                                        </TableCell>
                                                        <TableCell>
                                                            {field.isFaulty === null ? '-' : field.isFaulty ? 'Nee' : 'Ja'}
                                                        </TableCell>
                                                        <TableCell className="w-10">
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                onClick={() => handleDeleteUnusedMeasurement(field.ElectricalProjectRisoMeasurementId)}
                                                            >
                                                                <Trash2 className="h-4 w-4" />
                                                            </Button>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                )}
                            </>
                        );
                    }}
                </form.Subscribe>
            </div>
        </DragDropContext>
    );
}
